import { Injectable, signal } from '@angular/core';
import { Observable, finalize, of, shareReplay, tap } from 'rxjs';
import { ApiResponse } from '@core/auth/models/api-response.model';
import { LoadingService } from '@core/services/loading.service';
import {
    WhatsAppEmbeddedSignupAccount,
    WhatsAppEmbeddedSignupCompletePayload,
} from '@features/settings/interfaces/whatsapp-embedded-sigup-account.interface';
import { WhatsAppOnboardingService } from '@features/settings/services/whatsapp-onboarding.service';

@Injectable({
    providedIn: 'root',
})
export class WhatsAppOnboardingFacade {
    public accounts = signal<WhatsAppEmbeddedSignupAccount[]>([]);
    public readonly loading = signal(false);

    private cachedStatusResponse: ApiResponse<WhatsAppEmbeddedSignupAccount[]> | null = null;
    private cachedStatusExpiresAt = 0;
    private readonly cacheTtlMs = 1000 * 60 * 5;

    private statusRequest$: Observable<ApiResponse<WhatsAppEmbeddedSignupAccount[]>> | null = null;

    constructor(
        private readonly whatsappOnboardingApi: WhatsAppOnboardingService,
        private readonly loadingService: LoadingService,
    ) {}

    getStatus(forceRefresh = false): Observable<ApiResponse<WhatsAppEmbeddedSignupAccount[]>> {
        if (forceRefresh) {
            this.invalidateCache();
        }

        if (this.hasValidCache()) {
            console.debug('[PERF][WHATSAPP_ONBOARDING] cache hit');
            this.accounts.set(this.cachedStatusResponse!.data);
            return of(this.cachedStatusResponse!);
        }

        if (this.statusRequest$) {
            console.debug('[PERF][WHATSAPP_ONBOARDING] joined in-flight request');
            return this.statusRequest$;
        }
        console.log('getStatus');
        console.debug('[PERF][WHATSAPP_ONBOARDING] cache miss');
        this.loadingService.show();
        this.loading.set(true);

        this.statusRequest$ = this.whatsappOnboardingApi.getStatus().pipe(
            tap((response) => {
                console.log('response', response);
                this.cachedStatusResponse = response;
                this.cachedStatusExpiresAt = Date.now() + this.cacheTtlMs;
                this.accounts.set(response.data);
            }),
            finalize(() => {
                console.log('finalize');
                this.loadingService.hide();
                this.loading.set(false);
                this.statusRequest$ = null;
            }),
            shareReplay(1),
        );

        return this.statusRequest$;
    }

    complete(
        payload: WhatsAppEmbeddedSignupCompletePayload,
    ): Observable<ApiResponse<WhatsAppEmbeddedSignupAccount>> {
        this.loadingService.show();
        this.loading.set(true);
        console.log('payload', payload);
        
        return this.whatsappOnboardingApi.complete(payload).pipe(
            tap((response) => {
                const account = response.data;
                console.log('account', account);
                this.accounts.update((list) => {
                    const index = list.findIndex((item) => item.id === account.id);
                    if (index >= 0) {
                        const updated = [...list];
                        updated[index] = account;
                        return updated;
                    }
                    return [...list, account];
                });
                this.invalidateCache();
            }),
            finalize(() => {
                this.loadingService.hide();
                this.loading.set(false);
            }),
        );
    }

    private invalidateCache(): void {
        this.cachedStatusResponse = null;
        this.cachedStatusExpiresAt = 0;
    }

    private hasValidCache(): boolean {
        return !!this.cachedStatusResponse && Date.now() < this.cachedStatusExpiresAt;
    }
}
